import type { GroupBase, StylesConfig } from 'react-select';

interface SelectStyleOptions {
  isError?: boolean;
  isDisabled?: boolean;
}

/**
 * Shared react-select styling, driven by the tokens in styles/tokens.css so the
 * custom selects match native .form-input / .form-select controls exactly.
 */
export function selectStyles<O, IsMulti extends boolean = false>({
  isError = false,
  isDisabled = false,
}: SelectStyleOptions = {}): StylesConfig<O, IsMulti, GroupBase<O>> {
  return {
    control: (base, state) => ({
      ...base,
      minHeight: 'var(--control-h)',
      border: `1px solid ${
        isError ? 'var(--danger)' : state.isFocused ? 'var(--ink)' : 'var(--line)'
      }`,
      borderRadius: 'var(--radius)',
      boxShadow: state.isFocused ? (isError ? 'var(--danger-ring)' : 'var(--focus-ring)') : 'none',
      background: isDisabled ? 'var(--field-bg-disabled)' : 'var(--field-bg)',
      fontSize: 'var(--control-font)',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      transition: 'border-color 0.12s ease, box-shadow 0.12s ease',
      '&:hover': {
        borderColor: isError ? 'var(--danger)' : state.isFocused ? 'var(--ink)' : 'var(--line-strong)',
      },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 var(--control-pad-x)', gap: '4px' }),
    placeholder: (base) => ({ ...base, color: 'var(--text-faint)', margin: 0 }),
    singleValue: (base) => ({
      ...base,
      color: isDisabled ? 'var(--text-muted)' : 'var(--text)',
      margin: 0,
    }),
    input: (base) => ({ ...base, color: 'var(--text)', margin: 0, padding: 0 }),
    multiValue: (base) => ({
      ...base,
      margin: '2px 0',
      background: 'var(--ink-soft)',
      border: '1px solid rgba(44, 110, 154, 0.25)',
      borderRadius: 'var(--radius)',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: 'var(--ink)',
      fontSize: 'var(--label-font)',
      fontWeight: 600,
      padding: '1px 4px 1px 6px',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: 'var(--ink)',
      borderRadius: 0,
      ':hover': { background: 'rgba(44, 110, 154, 0.18)', color: 'var(--ink-hover)' },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base, state) => ({
      ...base,
      padding: '0 8px',
      color: state.isFocused ? 'var(--ink)' : 'var(--text-muted)',
      transition: 'transform 0.12s ease, color 0.12s ease',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
      ':hover': { color: 'var(--ink)' },
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: '0 4px',
      color: 'var(--text-faint)',
      ':hover': { color: 'var(--danger)' },
    }),
    loadingIndicator: (base) => ({ ...base, color: 'var(--ink)' }),
    menu: (base) => ({
      ...base,
      marginTop: 4,
      border: '1px solid var(--line)',
      borderRadius: 'var(--radius)',
      boxShadow: '0 6px 16px rgba(15, 23, 42, 0.12)',
      zIndex: 9999,
    }),
    menuList: (base) => ({ ...base, padding: '4px 0', maxHeight: 240 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
      ...base,
      padding: '8px var(--control-pad-x)',
      fontSize: 'var(--control-font)',
      color: state.isSelected ? '#fff' : 'var(--text)',
      backgroundColor: state.isSelected ? 'var(--ink)' : state.isFocused ? 'var(--ink-soft)' : 'transparent',
      cursor: 'pointer',
      ':active': { backgroundColor: state.isSelected ? 'var(--ink)' : 'rgba(44, 110, 154, 0.14)' },
    }),
    noOptionsMessage: (base) => ({ ...base, fontSize: 'var(--control-font)', color: 'var(--text-faint)' }),
    loadingMessage: (base) => ({ ...base, fontSize: 'var(--control-font)', color: 'var(--text-faint)' }),
  };
}
